import { bombAtlas } from '../animation/Theme';
import { BombAnimationController } from '../animation/BombAnimationController';
import Phaser from 'phaser';
import { TILE, BOMB_FUSE_MS } from '../constants';
import { BombState, type BombData } from '../types';

// ── Static id counter ────────────────────────────────────────────────────────
let _nextId = 1;

export class Bomb extends Phaser.GameObjects.Container {
  private readonly _data: BombData;
  private visual: BombAnimationController;

  constructor(
    scene:    Phaser.Scene,
    col:      number,
    row:      number,
    radius:   number,
    ownerId:  number,
    remote:   boolean = false,
    hasKick:  boolean = false,
  ) {
    const px = col * TILE + TILE / 2;
    const py = row * TILE + TILE / 2;
    super(scene, px, py);

    this._data = {
      id:       _nextId++,
      col,
      row,
      radius,
      ownerId,
      state:    BombState.PLACED,
      timer:    BOMB_FUSE_MS,
      hasKick,
      kickDirX: 0,
      kickDirY: 0,
      remote,
    };

    const shadow = scene.add.ellipse(0, 19, 32, 10, 0x000000, 0.25);
    const sprite = scene.add.sprite(0, 0, bombAtlas(), 'bombs/0');
    if (remote) sprite.setTint(0x99ddff);
    this.add([shadow, sprite]);
    this.visual = new BombAnimationController(sprite);
    this._data.state = BombState.ARMED;
    this.visual.update(0, BOMB_FUSE_MS);
    scene.add.existing(this);
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /** Snapshot of this bomb's data (defensive copy via spread). */
  get bombData(): BombData {
    return { ...this._data };
  }

  /** True when the fuse has burned down and the bomb should explode. */
  get shouldExplode(): boolean {
    return this._data.state === BombState.ARMED && this._data.timer <= 0;
  }

  /** Grid column. */
  get col(): number { return this._data.col; }

  /** Grid row. */
  get row(): number { return this._data.row; }

  /** Blast radius. */
  get radius(): number { return this._data.radius; }

  /** Owner player id. */
  get ownerId(): number { return this._data.ownerId; }

  /** Current BombState. Use bombState to avoid shadowing Phaser's Container.state property. */
  get bombState(): BombState { return this._data.state; }

  /** Remaining fuse time in ms. */
  get timer(): number { return this._data.timer; }

  /** Force immediate explosion (chain reaction). */
  triggerChain(): void {
    if (this._data.state === BombState.ARMED) {
      this._data.timer = 0;
    }
  }

  /** Apply a kick direction (from BombKick power-up). */
  setKick(dirX: number, dirY: number): void {
    this._data.kickDirX = dirX;
    this._data.kickDirY = dirY;
  }

  /**
   * Mark bomb as exploding. Called by BombSystem before creating explosion
   * cells, so that overlap logic ignores it.
   */
  beginExploding(): void {
    this._data.state = BombState.EXPLODING;
  }

  /**
   * Mark bomb as fully removed (called by BombSystem after explosion resolved).
   */
  markRemoved(): void {
    this._data.state = BombState.REMOVED;
  }

  // -------------------------------------------------------------------------
  // Update loop
  // -------------------------------------------------------------------------

  sync(data: BombData): void {
    Object.assign(this._data, data);
    this.setPosition(data.col * TILE + TILE / 2, data.row * TILE + TILE / 2);
  }

  update(delta: number): void {
    if (this._data.state === BombState.REMOVED ||
        this._data.state === BombState.EXPLODING) return;

    this.visual.update(delta, this._data.timer);
  }

}

export default Bomb;
